import { HttpResult } from 'widgets-for-react'
import { CMSNodeClient, FilterClient, OverviewCourseItemDTO } from '../../backendApi/generated'
import { PageResult, SharepointPageData } from '../cmsPage/sharepointPageApi/types/pageData'
import { PriceType } from '../models/Events/Events'
import { OpleidingFilter, OverviewCourseItem } from './opleidingenOverviewState'
import { rerouteSPUrls } from '../cmsPage/sharepointPageApi/transformers/blockDataToBlock'
import { getImageUrls } from '../../utils/sharepointImageUrls'
import { listingItemSrc, listingItemSrcSet } from '../../utils/imageSizes'
import { BlocksState } from '../cmsPage/blocks/blocksState'
import { transformSharepointPageData } from '../cmsPage/sharepointPageApi/transformers/pageDataToPage'
import { transformToBlocks } from '../cmsPage/sharepointPageApi/sharepointPageApi'

const searchClient = new CMSNodeClient(window.location.origin)
const filterClient = new FilterClient(window.location.origin)

export type FilterItem = {
  value: string
  isSelected: boolean
  docCount: number
}

type SelectFilter = {
  Kind: 'select'
  FilterItems: FilterItem[]
}

type SearchFilter = {
  Kind: 'search'
  SearchQuery: string
}

type DateFilter = {
  Kind: 'date'
  EndDate: Date
}

type OpleidingenFilters = {
  search: SearchFilter
  date: DateFilter
  CourseCategory: SelectFilter
}

export const fetchFilters = async (filtersUrlParam: string): Promise<HttpResult<OpleidingFilter[]>> => {
  const filterItems = filtersUrlParam.split(',')
  const res = await filterClient.getCourseFilters(null);

  const data = res as OpleidingenFilters
  return {
    kind: 'result',
    status: 200,
    value: data.CourseCategory.FilterItems.map((item) => ({
      name: item.value,
      availableCourses: item.docCount,
      isSelected: filterItems.includes(item.value)
    })),
  }
}

export const fetchOpleidingen = async (
  page: number,
  pageSize: number,
  filtersUrlParam: string,
  query: string
): Promise<HttpResult<PageResult<OverviewCourseItem>>> => {
  const filterItems = filtersUrlParam ? filtersUrlParam.split(',').map(f => ({ value: f, isSelected: true, docCount: 0 })) : []
  const filters: OpleidingenFilters = {
    search: {
      Kind: 'search',
      SearchQuery: query,
    },
    date: {
      Kind: 'date',
      EndDate: new Date(),
    },
    CourseCategory: {
      Kind: 'select',
      FilterItems: filterItems
    },
  }
  const searchFilters: any = { Filters: filters, Page: page, PageSize: pageSize }

  const res = await searchClient.getCourses(searchFilters)

  const courses: OverviewCourseItem[] = res.Rows?.map(courseDataToItem) || []

  return {
    kind: 'result',
    status: 200,
    value: {
      Resources: courses.map((course) => ({
        Resource: course,
        Highlight: {},
      })),
      Count: res.TotalItems,
      PerPage: pageSize,
      Total: res.TotalItems,
    },
  }
}

export const fetchOpleidingenBlocks = async (): Promise<HttpResult<BlocksState>> => {
  const res = await fetch(`/api/v1/pages/all/opleidingen`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const pageData: SharepointPageData = await res.json()

  const page = transformSharepointPageData(pageData)

  const blocks = transformToBlocks(pageData.Content, page)

  return {
    kind: 'result',
    status: 200,
    value: blocks,
  }
}

const courseDataToItem = (courseData: OverviewCourseItemDTO): OverviewCourseItem => {
  const item: OverviewCourseItem = {
    url: courseData.slug ?? '',
    title: courseData.teaserInfo?.title ?? '',
    label: courseData.teaserInfo?.label ?? '',
    newprice: courseData.prices?.find((product) => !product.AdditionalProduct && product.PriceType === PriceType.MEMBER)?.Amount?.toString() ?? '',
    oldprice: courseData.prices?.find((product) => !product.AdditionalProduct && product.PriceType === PriceType.NONMEMBER)?.Amount?.toString() ?? '',
    duration: courseData.duration ?? '',
    image: courseData.teaserInfo?.image?.Url ? {
      alt: courseData.teaserInfo?.image?.AlternativeText ?? '',
      src: listingItemSrc,
      srcset: listingItemSrcSet,
      sizes: 'auto',
      lazyload: true,
      width: '240',
      height: '140',
      urls: getImageUrls(rerouteSPUrls(courseData.teaserInfo?.image?.Url)),
      keepOriginalSize: false
    } : undefined
  }
  return item;
}

